import React, { useMemo, memo } from 'react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { PILLAR_INFO } from '@/lib/seeds';
import type { PillarSlug } from '@/types';

interface PillarScore {
  pillar: PillarSlug;
  currentWeek: number;
  lastWeek?: number;
}

interface PillarRadarChartProps {
  scores: PillarScore[];
  className?: string;
}

// Ordem fixa dos pilares no gráfico (para consistência visual)
const PILLAR_ORDER: PillarSlug[] = [
  'espiritual',
  'interior', 
  'profissional',
  'saudavel',
  'social',
  'sentimental'
];

export const PillarRadarChart = memo(({ scores, className }: PillarRadarChartProps) => {
  // ✅ FASE 9: Já tinha useMemo, agora com React.memo também
  const chartData = useMemo(() => {
    // Garantir que TODOS os 6 pilares aparecem na ordem correta
    return PILLAR_ORDER.map(pillarSlug => {
      const score = scores.find(s => s.pillar === pillarSlug);
      const pillarInfo = PILLAR_INFO[pillarSlug];
      const currentWeek = score?.currentWeek || 0;
      const hasActivity = currentWeek > 0;
      
      return {
        pillar: pillarInfo.name,
        pillarSlug,
        'Semana Atual': currentWeek,
        'Semana Anterior': score?.lastWeek || 0,
        fullMark: 10,
        hasActivity
      };
    });
  }, [scores]);

  return (
    <div className={className}>
      <ResponsiveContainer width="100%" height={400}>
        <RadarChart data={chartData}>
          <PolarGrid stroke="hsl(var(--border))" />
          <PolarAngleAxis 
            dataKey="pillar" 
            tick={{ fill: 'hsl(var(--foreground))', fontSize: 12 }}
          />
          <PolarRadiusAxis 
            angle={90} 
            domain={[0, 10]} 
            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
          />
          <Radar
            name="Semana Atual"
            dataKey="Semana Atual"
            stroke="hsl(var(--tdd-green-primary))"
            fill="hsl(var(--tdd-green-primary))"
            fillOpacity={0.6}
            strokeWidth={2}
          />
          {scores.some(s => s.lastWeek !== undefined) && (
            <Radar
              name="Semana Anterior"
              dataKey="Semana Anterior"
              stroke="hsl(var(--muted-foreground))"
              fill="hsl(var(--muted-foreground))"
              fillOpacity={0.3}
              strokeWidth={1}
              strokeDasharray="5 5"
            />
          )}
          <Tooltip 
            contentStyle={{
              backgroundColor: 'hsl(var(--card))',
              border: '1px solid hsl(var(--border))',
              borderRadius: '8px',
              color: 'hsl(var(--foreground))',
            }}
            formatter={(value: number, name: string, props: any) => {
              const current = props.payload['Semana Atual'];
              const last = props.payload['Semana Anterior'];
              const hasActivity = props.payload.hasActivity;
              
              let indicator = hasActivity ? '📊' : '⚪';
              let trend = '';
              
              if (hasActivity && last && last > 0) {
                trend = current > last ? ' 📈' : current < last ? ' 📉' : ' ➡️';
              }
              
              return [`${value}/10 ${indicator}${trend}`, name];
            }}
          />
          <Legend 
            wrapperStyle={{
              paddingTop: '20px',
            }}
          />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
});

PillarRadarChart.displayName = 'PillarRadarChart';
